import { memo, useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { eventsAPI } from '../../services/api/events'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { createLogger } from '../../utils/logger'
import { toast } from '../../services/toastService'
import {
  CalendarDaysIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  BoltIcon,
  NoSymbolIcon
} from '@heroicons/react/24/outline'

const logger = createLogger('AdminEventsSection')

/**
 * AdminEventsSection - Pop-up events management dashboard
 * @atomic @idempotent
 */
const AdminEventsSection = memo(function AdminEventsSection({ onBack }) {
  const { t } = useTranslation()
  const { execute } = useAsyncOperation()
  const navigate = useNavigate()

  const [events, setEvents] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [sourceFilter, setSourceFilter] = useState('')
  const [bulkApproving, setBulkApproving] = useState(false)

  const perPage = 20

  const loadEvents = useCallback(async (p = 1, source = sourceFilter) => {
    setLoading(true)
    await execute(
      () => eventsAPI.adminListEvents({ page: p, limit: perPage, source: source || undefined }),
      {
        onSuccess: (res) => {
          setEvents(res.data?.data || [])
          setTotal(res.data?.total || 0)
          setPage(p)
        },
        onError: (err) => logger.error('Failed to load events', err)
      }
    )
    setLoading(false)
  }, [execute, sourceFilter])

  useEffect(() => { loadEvents(1) }, [loadEvents])

  const handleSourceFilterChange = useCallback((value) => {
    setSourceFilter(value)
    loadEvents(1, value)
  }, [loadEvents])

  const handleBulkApprove = useCallback(async () => {
    setBulkApproving(true)
    await execute(() => eventsAPI.adminBulkPublishEvents(), {
      onSuccess: (res) => {
        const count = res.data?.published ?? 0
        toast.success(count > 0 ? t('admin.events.bulkApproveSuccess', { count }) : t('admin.events.bulkApproveNone'))
        loadEvents(page)
      },
      onError: (err) => toast.error(err.message)
    })
    setBulkApproving(false)
  }, [execute, loadEvents, page, t])

  const handlePublish = useCallback(async (ev) => {
    const fn = ev.isPublished ? eventsAPI.adminUnpublishEvent : eventsAPI.adminPublishEvent
    await execute(() => fn(ev.id), {
      onSuccess: () => {
        toast.success(ev.isPublished ? t('admin.events.unpublished') : t('admin.events.published'))
        loadEvents(page)
      },
      onError: (err) => toast.error(err.message)
    })
  }, [execute, loadEvents, page, t])

  const handleActivate = useCallback(async (ev) => {
    await execute(() => eventsAPI.adminActivateEvent(ev.id), {
      onSuccess: (res) => {
        toast.success(t('admin.events.activated', { count: res.data?.sightingsCreated ?? 0 }))
        loadEvents(page)
      },
      onError: (err) => toast.error(err.message)
    })
  }, [execute, loadEvents, page, t])

  const handleDeactivate = useCallback(async (ev) => {
    await execute(() => eventsAPI.adminDeactivateEvent(ev.id), {
      onSuccess: () => {
        toast.success(t('admin.events.deactivated'))
        loadEvents(page)
      },
      onError: (err) => toast.error(err.message)
    })
  }, [execute, loadEvents, page, t])

  const handleDelete = useCallback(async (ev) => {
    if (!window.confirm(t('admin.events.confirmDelete', { title: ev.title }))) return
    await execute(() => eventsAPI.adminDeleteEvent(ev.id), {
      onSuccess: () => {
        toast.success(t('admin.events.deleted'))
        loadEvents(page)
      },
      onError: (err) => toast.error(err.message)
    })
  }, [execute, loadEvents, page, t])

  const totalPages = Math.ceil(total / perPage)
  const now = new Date()

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header */}
      <div className="px-4 lg:px-6 py-4 border-b border-gray-200 flex flex-wrap justify-between items-center gap-3">
        <h2 className="text-xl font-semibold text-gray-900">{t('admin.events.title')}</h2>
        <div className="flex items-center gap-3">
          <select
            value={sourceFilter}
            onChange={(e) => handleSourceFilterChange(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-2 py-2 text-gray-600"
            aria-label={t('admin.events.sourceLabel')}
          >
            <option value="">{t('admin.events.sourceAll')}</option>
            <option value="admin">{t('admin.events.sourceAdmin')}</option>
            <option value="pipeline">{t('admin.events.sourcePipeline')}</option>
          </select>
          <button
            onClick={handleBulkApprove}
            onTouchEnd={handleBulkApprove}
            disabled={bulkApproving}
            className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm font-medium text-gray-600 disabled:opacity-50"
          >
            {t('admin.events.bulkApprovePipeline')}
          </button>
          <button
            onClick={() => navigate('/admin/events/new')}
            onTouchEnd={() => navigate('/admin/events/new')}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 text-sm font-medium flex items-center gap-1.5"
          >
            <PlusIcon className="h-4 w-4" />
            {t('admin.events.create')}
          </button>
          {onBack && (
            <button onClick={onBack} className="text-sm text-primary hover:text-primary/80">
              ← {t('common.back', 'Back to Overview')}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.events.col.title')}</th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.events.col.date')}</th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.events.col.participants')}</th>
              <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.events.col.status')}</th>
              <th className="px-4 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('admin.events.col.actions')}</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="5" className="px-4 lg:px-6 py-8 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </td>
              </tr>
            ) : events.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-4 lg:px-6 py-12 text-center">
                  <CalendarDaysIcon className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                  <p className="text-gray-500">{t('admin.events.empty')}</p>
                </td>
              </tr>
            ) : (
              events.map((ev) => {
                const start = new Date(ev.startAt)
                const end = new Date(ev.endAt)
                const isLive = now >= start && now <= end
                const isPast = now > end

                return (
                  <tr key={ev.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 lg:px-6 py-4">
                      <div className="font-medium text-gray-900 max-w-xs truncate">{ev.title}</div>
                      {ev.locationName && <div className="text-xs text-gray-400 truncate">{ev.locationName}</div>}
                    </td>
                    <td className="px-4 lg:px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                      <div>{start.toLocaleDateString()}</div>
                      <div className="text-xs text-gray-400">{start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} – {end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                    </td>
                    <td className="px-4 lg:px-6 py-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        {ev.participantCount}
                      </span>
                    </td>
                    <td className="px-4 lg:px-6 py-4">
                      <div className="flex flex-col gap-1">
                        {ev.isPublished ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckIcon className="h-3 w-3" /> {t('admin.events.status.published')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            <XMarkIcon className="h-3 w-3" /> {t('admin.events.status.draft')}
                          </span>
                        )}
                        {isLive && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            <BoltIcon className="h-3 w-3" /> {t('admin.events.status.live')}
                          </span>
                        )}
                        {isPast && (
                          <span className="text-xs text-gray-400">{t('admin.events.status.past')}</span>
                        )}
                        {ev.source === 'pipeline' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {t('admin.events.sourcePipeline')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {!isPast && (
                          <button
                            onClick={() => handlePublish(ev)}
                            onTouchEnd={() => handlePublish(ev)}
                            className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 text-gray-600 transition-colors"
                          >
                            {ev.isPublished ? t('admin.events.unpublish') : t('admin.events.publish')}
                          </button>
                        )}
                        {ev.isPublished && !isPast && (
                          isLive ? (
                            <button
                              onClick={() => handleDeactivate(ev)}
                              onTouchEnd={() => handleDeactivate(ev)}
                              className="text-xs px-2 py-1 rounded border border-red-300 hover:bg-red-50 text-red-600 transition-colors flex items-center gap-1"
                            >
                              <NoSymbolIcon className="h-3 w-3" /> {t('admin.events.deactivate')}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleActivate(ev)}
                              onTouchEnd={() => handleActivate(ev)}
                              className="text-xs px-2 py-1 rounded border border-orange-300 hover:bg-orange-50 text-orange-600 transition-colors flex items-center gap-1"
                            >
                              <BoltIcon className="h-3 w-3" /> {t('admin.events.activate')}
                            </button>
                          )
                        )}
                        <button
                          onClick={() => navigate(`/admin/events/${ev.id}`)}
                          onTouchEnd={() => navigate(`/admin/events/${ev.id}`)}
                          className="p-1.5 text-gray-400 hover:text-primary hover:bg-gray-100 rounded transition-colors"
                          title={t('common.edit')}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(ev)}
                          onTouchEnd={() => handleDelete(ev)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title={t('common.delete')}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 lg:px-6 py-3 border-t border-gray-200 flex items-center justify-between text-sm text-gray-500">
          <span>{t('admin.events.pagination', { from: (page - 1) * perPage + 1, to: Math.min(page * perPage, total), total })}</span>
          <div className="flex gap-2">
            <button
              disabled={page === 1}
              onClick={() => loadEvents(page - 1)}
              className="px-3 py-1 rounded border disabled:opacity-40 hover:bg-gray-50 transition-colors"
            >
              {t('common.prev')}
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => loadEvents(page + 1)}
              className="px-3 py-1 rounded border disabled:opacity-40 hover:bg-gray-50 transition-colors"
            >
              {t('common.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
})

export default AdminEventsSection
